import * as XLSX from 'xlsx';
import RandomIdGenerator from './RandomIdGenerator';

/**
 * Processes Excel files to extract TaskSetProfile data.
 * Parses sheets with hierarchical task/subtask structure.
 */
export class TaskSetProfileExcelProcessor {
  constructor() {
    this.errors = [];
    this.warnings = [];
    this.idGenerator = new RandomIdGenerator(10);

    // Reference sheets to skip when parsing profiles
    this.REFERENCE_SHEETS = ['Teams', 'Templates'];
  }

  /**
   * Process an Excel file and extract TaskSetProfiles.
   * @param {ArrayBuffer|Uint8Array} fileBuffer - Excel file as ArrayBuffer or Uint8Array
   * @returns {Object} { profiles: TaskSetProfile[], errors: [], warnings: [], isValid: boolean }
   */
  processFile(fileBuffer) {
    this.errors = [];
    this.warnings = [];

    const workbook = XLSX.read(fileBuffer, {
      type: 'array',
      cellDates: true
    });

    const profiles = [];

    // Filter out reference sheets
    const profileSheets = workbook.SheetNames.filter(
      name => !this.REFERENCE_SHEETS.includes(name)
    );

    if (profileSheets.length === 0) {
      this.errors.push({
        sheet: '',
        row: 0,
        column: '',
        message: 'No profile sheets found in workbook. Reference sheets (Teams, Templates) are not processed.'
      });
      return this.buildResult(profiles);
    }

    for (const sheetName of profileSheets) {
      try {
        const profile = this.processSheet(workbook.Sheets[sheetName], sheetName);
        if (profile) {
          profiles.push(profile);
        }
      } catch (error) {
        this.errors.push({
          sheet: sheetName,
          row: 0,
          column: '',
          message: `Failed to process sheet: ${error.message}`
        });
      }
    }

    return this.buildResult(profiles);
  }

  /**
   * Build the result object.
   * @param {Array} profiles - Parsed profiles
   * @returns {Object} Result with profiles, errors, warnings, isValid
   */
  buildResult(profiles) {
    return {
      profiles,
      errors: this.errors,
      warnings: this.warnings,
      isValid: this.errors.length === 0
    };
  }

  /**
   * Process a single worksheet into a TaskSetProfile.
   * @param {Object} worksheet - XLSX worksheet object
   * @param {string} sheetName - Name of the sheet
   * @returns {Object|null} TaskSetProfile object or null if empty
   */
  processSheet(worksheet, sheetName) {
    // Parse sheet name to extract code and title
    // Format: "{code} | {title}" or just "{code}"
    const { code, title } = this.parseSheetName(sheetName);

    // Convert worksheet to JSON array (array of arrays)
    const rows = XLSX.utils.sheet_to_json(worksheet, {
      header: 1,
      defval: ''  // Default empty cells to empty string
    });

    if (rows.length < 2) {
      this.warnings.push({
        sheet: sheetName,
        row: 0,
        column: '',
        message: 'Sheet has no data rows (only header or empty)'
      });
      return null;
    }

    // Parse header row to get column indices
    const headers = rows[0];
    const columnIndices = this.getColumnIndices(headers);

    if (columnIndices.taskLevelColumns.length === 0) {
      this.errors.push({
        sheet: sheetName,
        row: 1,
        column: '',
        message: 'No Task Level columns found in header row'
      });
      return null;
    }

    // Process data rows
    const taskProfiles = this.processRows(rows.slice(1), columnIndices, sheetName);

    return {
      code: code,
      title: title,
      taskProfiles: taskProfiles,
      // These are managed by RDrive API, not editable in Excel
      key: '',
      isPublished: false,
      current: true,
      publishMessage: ''
    };
  }

  /**
   * Parse sheet name to extract code and title.
   * @param {string} sheetName - Sheet name in format "{code} | {title}"
   * @returns {Object} { code, title }
   */
  parseSheetName(sheetName) {
    const separatorIndex = sheetName.indexOf(' | ');

    if (separatorIndex === -1) {
      // No separator - use entire name as both code and title
      return {
        code: sheetName.trim(),
        title: sheetName.trim()
      };
    }

    const code = sheetName.substring(0, separatorIndex).trim();
    const title = sheetName.substring(separatorIndex + 3).trim() || code;

    return { code, title };
  }

  /**
   * Get column indices from header row.
   * @param {Array} headers - Header row array
   * @returns {Object} Column indices mapping
   */
  getColumnIndices(headers) {
    const indices = {
      versionReference: -1,
      code: -1,
      taskLevelColumns: [],
      taskCodes: -1,
      subtask_title: -1,
      subtask_versionReference: -1,
      subtask_defaultLeadByTeamId: -1,
      subtask_weighting: -1,
      subtask_itemType: -1,
      subtask_itemTemplateCode: -1
    };

    headers.forEach((header, index) => {
      if (header == null) return;

      const h = header.toString().trim().toLowerCase();

      if (h === 'versionreference') {
        indices.versionReference = index;
      } else if (h === 'code') {
        indices.code = index;
      } else if (h.startsWith('task level')) {
        // Keep track of task level columns in order
        indices.taskLevelColumns.push(index);
      } else if (h === 'task codes') {
        indices.taskCodes = index;
      } else if (h === 'subtask title') {
        indices.subtask_title = index;
      } else if (h === 'subtask versionref') {
        indices.subtask_versionReference = index;
      } else if (h === 'lead team') {
        indices.subtask_defaultLeadByTeamId = index;
      } else if (h === 'weighting') {
        indices.subtask_weighting = index;
      } else if (h === 'item type') {
        indices.subtask_itemType = index;
      } else if (h === 'template') {
        indices.subtask_itemTemplateCode = index;
      }
    });

    // Sort task level columns by their index to ensure proper ordering
    indices.taskLevelColumns.sort((a, b) => a - b);

    return indices;
  }

  /**
   * Process data rows to build task hierarchy.
   * @param {Array} dataRows - Array of row arrays (excluding header)
   * @param {Object} columnIndices - Column index mapping
   * @param {string} sheetName - Sheet name for error reporting
   * @returns {Array} Array of TaskProfile objects (top-level)
   */
  processRows(dataRows, columnIndices, sheetName) {
    const rootTasks = [];
    let currentTask = null;
    const taskStack = []; // Stack of { task, depth } to track hierarchy

    dataRows.forEach((row, rowIndex) => {
      const excelRowNum = rowIndex + 2; // +2 for header and 0-indexing

      // Skip completely empty rows
      if (this.isEmptyRow(row)) {
        return;
      }

      const rowType = this.getRowType(row, columnIndices);

      if (rowType === 'task') {
        const taskResult = this.parseTaskRow(row, columnIndices, excelRowNum, sheetName);

        if (!taskResult) {
          return; // Error already recorded
        }

        const { taskProfile, depth } = taskResult;

        // Determine where this task belongs in hierarchy
        if (depth === 0) {
          // Root level task
          rootTasks.push(taskProfile);
          taskStack.length = 0;
          taskStack.push({ task: taskProfile, depth: 0 });
        } else {
          // Find appropriate parent by popping tasks at same or deeper level
          while (taskStack.length > 0 && taskStack[taskStack.length - 1].depth >= depth) {
            taskStack.pop();
          }

          if (taskStack.length > 0) {
            // Add as child of the task at the top of the stack
            const parentEntry = taskStack[taskStack.length - 1];
            parentEntry.task.children.push(taskProfile);
          } else {
            // No parent found at shallower depth - add as root
            // This handles cases where data might skip levels
            rootTasks.push(taskProfile);
          }

          taskStack.push({ task: taskProfile, depth: depth });
        }

        currentTask = taskProfile;

      } else if (rowType === 'subtask') {
        if (!currentTask) {
          this.errors.push({
            sheet: sheetName,
            row: excelRowNum,
            column: 'Subtask Title',
            message: 'Subtask row found without a preceding task row'
          });
          return;
        }

        const subtask = this.parseSubtaskRow(row, columnIndices, excelRowNum, sheetName);
        if (subtask) {
          currentTask.subtaskProfiles.push(subtask);
        }
      }
      // Skip rows that are neither task nor subtask (extra empty/formatting rows)
    });

    return rootTasks;
  }

  /**
   * Check if a row is completely empty.
   * @param {Array} row - Row array
   * @returns {boolean} True if row is empty
   */
  isEmptyRow(row) {
    return !row.some(cell => cell != null && cell.toString().trim() !== '');
  }

  /**
   * Determine the type of a row.
   * @param {Array} row - Row array
   * @param {Object} columnIndices - Column indices
   * @returns {string} 'task', 'subtask', or 'empty'
   */
  getRowType(row, columnIndices) {
    // Check if any Task Level column has content
    const hasTaskLevel = columnIndices.taskLevelColumns.some(idx => {
      const value = row[idx];
      return value != null && value.toString().trim() !== '';
    });

    // Check if Subtask Title has content
    const hasSubtaskTitle = columnIndices.subtask_title >= 0 &&
      row[columnIndices.subtask_title] != null &&
      row[columnIndices.subtask_title].toString().trim() !== '';

    if (hasTaskLevel) {
      return 'task';
    } else if (hasSubtaskTitle) {
      return 'subtask';
    }

    return 'empty';
  }

  /**
   * Parse a task row into a TaskProfile object.
   * @param {Array} row - Row array
   * @param {Object} columnIndices - Column indices
   * @param {number} excelRowNum - Excel row number for error reporting
   * @param {string} sheetName - Sheet name for error reporting
   * @returns {Object|null} { taskProfile, depth } or null on error
   */
  parseTaskRow(row, columnIndices, excelRowNum, sheetName) {
    // Find the Task Level column with content and determine depth
    let depth = -1;
    let title = null;

    for (let i = 0; i < columnIndices.taskLevelColumns.length; i++) {
      const idx = columnIndices.taskLevelColumns[i];
      const value = row[idx];
      if (value != null && value.toString().trim() !== '') {
        depth = i;
        title = value.toString().trim();
        break; // Use the leftmost populated Task Level column
      }
    }

    if (depth === -1 || !title) {
      this.errors.push({
        sheet: sheetName,
        row: excelRowNum,
        column: 'Task Level',
        message: 'Task row has no title in any Task Level column'
      });
      return null;
    }

    const versionReference = this.getCellValue(row, columnIndices.versionReference) ||
                              this.generateVersionReference();
    const code = this.getCellValue(row, columnIndices.code) || '';
    const taskCodes = this.parseTaskCodes(this.getCellValue(row, columnIndices.taskCodes));

    const taskProfile = {
      versionReference: versionReference,
      code: code,
      title: title,
      taskCodes: taskCodes,
      subtaskProfiles: [],
      children: []
    };

    return { taskProfile, depth };
  }

  /**
   * Parse a subtask row into a SubtaskProfile object.
   * @param {Array} row - Row array
   * @param {Object} columnIndices - Column indices
   * @param {number} excelRowNum - Excel row number for error reporting
   * @param {string} sheetName - Sheet name for error reporting
   * @returns {Object|null} SubtaskProfile object or null on error
   */
  parseSubtaskRow(row, columnIndices, excelRowNum, sheetName) {
    const title = this.getCellValue(row, columnIndices.subtask_title);

    if (!title) {
      // This shouldn't happen since we check for subtask title in getRowType
      return null;
    }

    const versionReference = this.getCellValue(row, columnIndices.subtask_versionReference) ||
                              this.generateVersionReference();

    // Parse Lead Team - may be in format "Team Name (ID)" or just the ID
    const rawLeadTeam = this.getCellValue(row, columnIndices.subtask_defaultLeadByTeamId);
    const defaultLeadByTeamId = this.parseTeamId(rawLeadTeam);

    const weighting = this.parseWeighting(
      this.getCellValue(row, columnIndices.subtask_weighting),
      excelRowNum,
      sheetName
    );

    // Parse Template - may be in format "Title (Code - Type)" or just the code
    const rawTemplate = this.getCellValue(row, columnIndices.subtask_itemTemplateCode);
    const { code: itemTemplateCode, type: parsedItemType } = this.parseTemplate(rawTemplate);

    // Item Type can come from a dedicated column or be parsed from Template
    let itemType = this.getCellValue(row, columnIndices.subtask_itemType) || parsedItemType;

    // Validate and normalize itemType
    if (itemType) {
      const normalizedType = itemType.toLowerCase();
      if (!['issue', 'process'].includes(normalizedType)) {
        this.errors.push({
          sheet: sheetName,
          row: excelRowNum,
          column: 'Item Type',
          message: `Invalid item type: "${itemType}". Must be "issue" or "process"`
        });
        itemType = null;
      } else {
        itemType = normalizedType;
      }
    }

    return {
      versionReference: versionReference,
      title: title,
      defaultLeadByTeamId: defaultLeadByTeamId,
      weighting: weighting,
      itemType: itemType,
      itemTemplateCode: itemTemplateCode
    };
  }

  /**
   * Parse team ID from formatted string.
   * Handles format "Team Name (ID)" or just "ID".
   * @param {string} value - Raw team value
   * @returns {string|null} Team ID or null
   */
  parseTeamId(value) {
    if (!value) return null;

    // Try to extract ID from format "Team Name (ID)"
    const match = value.match(/\(([^)]+)\)\s*$/);
    if (match) {
      return match[1].trim();
    }

    // Otherwise assume the value is already the ID
    return value;
  }

  /**
   * Parse template code and type from formatted string.
   * Handles formats:
   *   - "Title (Code - Type)" → { code: "Code", type: "Type" }
   *   - "Title (Code - )" → { code: "Code", type: null }
   *   - "Title (Code)" → { code: "Code", type: null }
   *   - "Code" → { code: "Code", type: null }
   * @param {string} value - Raw template value
   * @returns {Object} { code: string|null, type: string|null }
   */
  parseTemplate(value) {
    if (!value) return { code: null, type: null };

    // Try to extract code and type from format "Title (Code - Type)"
    const matchWithType = value.match(/\(([^)]+)\s*-\s*([^)]+)\)\s*$/);
    if (matchWithType) {
      return {
        code: matchWithType[1].trim(),
        type: matchWithType[2].trim()
      };
    }

    // Try format "Title (Code - )" where type is empty
    const matchCodeDash = value.match(/\(([^)]+)\s*-\s*\)\s*$/);
    if (matchCodeDash) {
      return {
        code: matchCodeDash[1].trim(),
        type: null
      };
    }

    // Try format "Title (Code)" without type
    const matchCodeOnly = value.match(/\(([^)]+)\)\s*$/);
    if (matchCodeOnly) {
      return {
        code: matchCodeOnly[1].trim(),
        type: null
      };
    }

    // Otherwise assume the value is already the code
    return { code: value, type: null };
  }

  /**
   * Get a cell value as a trimmed string.
   * @param {Array} row - Row array
   * @param {number} index - Column index
   * @returns {string|null} Cell value or null
   */
  getCellValue(row, index) {
    if (index < 0 || index >= row.length) return null;
    const value = row[index];
    if (value == null) return null;
    const stringValue = value.toString().trim();
    return stringValue === '' ? null : stringValue;
  }

  /**
   * Parse task codes from comma-separated string.
   * @param {string} value - Comma-separated task codes
   * @returns {Array} Array of task code strings
   */
  parseTaskCodes(value) {
    if (!value) return [];
    return value
      .split(',')
      .map(s => s.trim())
      .filter(s => s.length > 0);
  }

  /**
   * Parse weighting value.
   * @param {string} value - Weighting value
   * @param {number} excelRowNum - Excel row number for warning
   * @param {string} sheetName - Sheet name for warning
   * @returns {number} Parsed weighting (defaults to 1)
   */
  parseWeighting(value, excelRowNum, sheetName) {
    if (value == null || value === '') {
      return 1; // Default weighting
    }

    const num = parseInt(value, 10);

    if (isNaN(num)) {
      this.warnings.push({
        sheet: sheetName,
        row: excelRowNum,
        column: 'Weighting',
        message: `Invalid weighting value: "${value}". Using default of 1`
      });
      return 1;
    }

    if (num < 0) {
      this.warnings.push({
        sheet: sheetName,
        row: excelRowNum,
        column: 'Weighting',
        message: `Negative weighting value: ${num}. Using 0 instead`
      });
      return 0;
    }

    return num;
  }

  /**
   * Generate a unique version reference.
   * @returns {string} Generated version reference
   */
  generateVersionReference() {
    return 'vr_' + this.idGenerator.generate();
  }
}

export default TaskSetProfileExcelProcessor;
